import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import VertebraSchemeIcon from "../../../assets/images/radiodensity/Vertebra scheme.svg";
import { vertebraGroups } from "../../../classes/enums/AnnotationsEnum";
import { ButtonSize } from "../../../classes/enums/ButtonEnum";
import { EventsEnum } from "../../../classes/enums/EventsEnum";
import { MedicalImagesType } from "../../../classes/enums/MedicalImagesEnum";
import { PLANNING_STATES } from "../../../classes/enums/PlanningStateEnum";
import { vertebraUtils } from "../../../classes/utils/AnnotationsUtils";
import { debounce } from "../../../classes/utils/OptimizationUtils";
import {
  generateCacheKey,
  generateStateKey,
} from "../../../classes/utils/RemoteRenderingUtils";
import IconButton from "../../../components/iconButton/IconButton";
import ImageViewer from "../../../components/imageViewer/ImageViewer";
import IncrementDecrementButtons from "../../../components/incrementDecrementButtons/IncrementDecrementButtons";
import LabeledSlider from "../../../components/labeledSlider/LabeledSlider";
import MeasurementDisplay from "../../../components/measurementDisplay/MeasurementDisplay";
import NeutralToggle from "../../../components/neutralToggle/NeutralToggle";
import RemoteViewer from "../../../components/remoteViewer/RemoteViewer";
import LabeledRangeSelect from "../../../components/select/LabeledRangeSelect";
import Select from "../../../components/select/Select";
import "./RadioDensity.css";

const DEFAULT_HEIGHT = 15;
const DEFAULT_DIAMETER = 20;

// Normalize view name to display settings key
const getDisplaySettingsViewType = viewName => {
  if (!viewName) return null;
  const lower = viewName.toLowerCase();
  if (lower.includes("lateral") || lower.includes("sagittal")) return "sagittal";
  if (lower.includes("front") || lower.includes("coronal")) return "coronal";
  if (lower.includes("axial")) return "axial";
  return null;
};

const RadioDensity = ({
  selectedTwin,
  areViewsReady,
  selectedModality,
  groupedThumbnailsStandard,
  twinId,
  digitalTwinSurgeryCache,
  renderClient,
  annotations,
  onStatisticsUpdate,
  planId,
  initialVertebraDimensions,
}) => {
  const [selectedDrrView, setSelectedDrrView] = useState("sagittal");
  const [selectedVertebrae, setSelectedVertebrae] = useState([]);
  // Per-vertebra dimensions map: { entityId: { height, diameter } }
  const [vertebraDimensions, setVertebraDimensions] = useState({});
  const [selectedAxialVertebra, setSelectedAxialVertebra] = useState(null);
  const [blackAndWhiteProjection, setBlackAndWhiteProjection] = useState(false);
  const [hasInitializedFromProps, setHasInitializedFromProps] = useState(false);

  const debouncedUpdateRef = useRef(null);

  // Sync state with initialVertebraDimensions when they become available from DB
  // initialVertebraDimensions is keyed by entity ID (number)
  useEffect(() => {
    if (
      !hasInitializedFromProps &&
      initialVertebraDimensions &&
      Object.keys(initialVertebraDimensions).length > 0
    ) {
      setVertebraDimensions(initialVertebraDimensions);
      setHasInitializedFromProps(true);
    }
  }, [initialVertebraDimensions, hasInitializedFromProps]);

  // Derived displayed values - show first selected vertebra's dimensions
  const displayedDiameter = useMemo(() => {
    if (selectedVertebrae.length === 0) return DEFAULT_DIAMETER;
    const firstVertebra = selectedVertebrae[0];
    const entityId = vertebraUtils.getVertebraId(firstVertebra);
    return vertebraDimensions[entityId]?.diameter ?? DEFAULT_DIAMETER;
  }, [selectedVertebrae, vertebraDimensions]);

  const displayedHeight = useMemo(() => {
    if (selectedVertebrae.length === 0) return DEFAULT_HEIGHT;
    const firstVertebra = selectedVertebrae[0];
    const entityId = vertebraUtils.getVertebraId(firstVertebra);
    return vertebraDimensions[entityId]?.height ?? DEFAULT_HEIGHT;
  }, [selectedVertebrae, vertebraDimensions]);

  const remoteKey = useMemo(
    () => generateStateKey(PLANNING_STATES.CHECK, twinId),
    [twinId]
  );

  const availableVertebrae = useMemo(() => {
    if (!annotations || !groupedThumbnailsStandard) return [];

    const vertebraeSet = new Set();

    const ctModalityData = groupedThumbnailsStandard.find(
      item => item.modality === "CT"
    );

    if (ctModalityData && ctModalityData.instances.length > 0) {
      const ctInstance = ctModalityData.instances[0];
      const instanceId = ctInstance._id;

      if (annotations[instanceId]) {
        const instanceAnnotations = annotations[instanceId];
        if (Array.isArray(instanceAnnotations)) {
          instanceAnnotations.forEach(annotation => {
            if (annotation.unique_identifier) {
              const vertebra = vertebraUtils.getVertebraFromId(
                annotation.unique_identifier
              );
              if (vertebra) {
                vertebraeSet.add(vertebra);
              }
            }
          });
        }
      }
    }

    const allVertebrae = [
      ...vertebraGroups.CERVICAL,
      ...vertebraGroups.THORACIC,
      ...vertebraGroups.LUMBAR,
    ];

    const sortedVertebrae = allVertebrae.filter(v => vertebraeSet.has(v));

    return sortedVertebrae;
  }, [annotations, groupedThumbnailsStandard]);

  const vertebraItems = useMemo(() => {
    const items = [{ value: "-", label: "-" }];

    availableVertebrae.forEach(vertebra => {
      items.push({
        value: vertebra,
        label: vertebraUtils.getVertebraLabel(vertebra),
      });
    });

    return items;
  }, [availableVertebrae]);

  // Set initial axial vertebra when views become ready
  // Cylinder initialization is handled by useRadioDensityManager with per-vertebra dimensions
  useEffect(() => {
    if (availableVertebrae.length > 0 && renderClient && areViewsReady) {
      const initializeAxialView = async () => {
        try {
          const lowestVertebra =
            availableVertebrae[availableVertebrae.length - 1];
          setSelectedAxialVertebra(lowestVertebra);

          const lowestEntityId = vertebraUtils.getVertebraId(lowestVertebra);
          if (lowestEntityId) {
            await renderClient.setAxialRadiodensityVertebra(
              remoteKey,
              lowestEntityId
            );
          }
        } catch (error) {}
      };

      initializeAxialView();
    }
  }, [availableVertebrae, renderClient, areViewsReady, remoteKey]);

  const handleAxialVertebraChange = useCallback(
    async selectedItem => {
      const value = selectedItem.target
        ? selectedItem.target.value
        : selectedItem.value;
      const vertebra = value === "-" ? null : value;
      setSelectedAxialVertebra(vertebra);

      if (renderClient && vertebra) {
        const entityId = vertebraUtils.getVertebraId(vertebra);
        if (entityId) {
          try {
            await renderClient.setAxialRadiodensityVertebra(
              remoteKey,
              entityId
            );
          } catch (error) {}
        }
      }
    },
    [remoteKey, renderClient]
  );

  useEffect(() => {
    const handleVertebraSelected = event => {
      // Extract entityId from the array structure
      const eventData = Array.isArray(event.detail)
        ? event.detail[0]
        : event.detail;
      const entityId = eventData?.entityId || eventData?.data?.entityId;

      if (entityId) {
        const vertebra = vertebraUtils.getVertebraFromId(entityId);
        if (vertebra && availableVertebrae.includes(vertebra)) {
          // Call the handler directly with the vertebra value
          handleAxialVertebraChange({ value: vertebra });
        } else {
        }
      }
    };

    window.addEventListener(
      EventsEnum.RADIO_DENSITY_VERTEBRA_SELECTED,
      handleVertebraSelected
    );

    return () => {
      window.removeEventListener(
        EventsEnum.RADIO_DENSITY_VERTEBRA_SELECTED,
        handleVertebraSelected
      );
    };
  }, [availableVertebrae, handleAxialVertebraChange]);

  useEffect(() => {
    if (!debouncedUpdateRef.current) {
      debouncedUpdateRef.current = debounce(
        async (vertebraeIds, diameter, height, allDimensions) => {
          if (!renderClient || vertebraeIds.length === 0) return;

          try {
            const result = await renderClient.updateRadiodensityCylinders(
              remoteKey,
              vertebraeIds,
              diameter,
              height
            );

            if (result && onStatisticsUpdate) {
              // Pass the full vertebraDimensions map for per-vertebra sync
              onStatisticsUpdate(result, allDimensions);
            }
          } catch (error) {}
        },
        200
      );
    }
  }, [renderClient, onStatisticsUpdate, remoteKey]);

  // Trigger update when selected vertebrae dimensions change
  useEffect(() => {
    if (debouncedUpdateRef.current && selectedVertebrae.length > 0) {
      const vertebraeEntityIds = selectedVertebrae
        .map(vertebra => vertebraUtils.getVertebraId(vertebra))
        .filter(id => id != null);

      // Get dimensions for the first selected vertebra (all selected have same value after change)
      const firstEntityId = vertebraeEntityIds[0];
      const dims = vertebraDimensions[firstEntityId] || {
        diameter: DEFAULT_DIAMETER,
        height: DEFAULT_HEIGHT,
      };

      debouncedUpdateRef.current(
        vertebraeEntityIds,
        dims.diameter,
        dims.height,
        vertebraDimensions
      );
    }
  }, [selectedVertebrae, vertebraDimensions]);

  const selectedVertebraItem = useMemo(() => {
    const firstVertebra =
      selectedVertebrae.length > 0 ? selectedVertebrae[0] : null;

    if (!firstVertebra || firstVertebra === "-") {
      return vertebraItems[0];
    }

    const foundItem = vertebraItems.find(item => item.value === firstVertebra);
    return foundItem || vertebraItems[0];
  }, [selectedVertebrae, vertebraItems]);

  const lastSelectedVertebraItem = useMemo(() => {
    const lastVertebra =
      selectedVertebrae.length > 1
        ? selectedVertebrae[selectedVertebrae.length - 1]
        : null;

    if (!lastVertebra || lastVertebra === "-") {
      return vertebraItems[0];
    }

    const foundItem = vertebraItems.find(item => item.value === lastVertebra);
    return foundItem || vertebraItems[0];
  }, [selectedVertebrae, vertebraItems]);

  const { sagittalDrrView, coronalDrrView, axialRadiodensityView } =
    useMemo(() => {
      let sagittalView = null;
      let coronalView = null;
      let axialView = null;

      if (selectedTwin && areViewsReady && selectedModality === "CT") {
        const modalityData = groupedThumbnailsStandard.find(
          item => item.modality === "CT"
        );

        if (modalityData) {
          modalityData.instances.forEach(instance => {
            const cacheKey = generateCacheKey(
              instance,
              twinId,
              PLANNING_STATES.CHECK
            );

            if (digitalTwinSurgeryCache.hasView(cacheKey)) {
              const cachedData = digitalTwinSurgeryCache.getView(cacheKey);
              if (Array.isArray(cachedData)) {
                const sagittalViewData = cachedData.find(
                  view => view.viewName === MedicalImagesType.SAGITTAL_DRR_CT
                );
                if (sagittalViewData) {
                  sagittalView = {
                    key: `${cacheKey}_${MedicalImagesType.SAGITTAL_DRR_CT}`,
                    viewData: sagittalViewData,
                    instance,
                    viewName: sagittalViewData.viewName,
                  };
                }

                const coronalViewData = cachedData.find(
                  view => view.viewName === MedicalImagesType.CORONAL_DRR_CT
                );
                if (coronalViewData) {
                  coronalView = {
                    key: `${cacheKey}_${MedicalImagesType.CORONAL_DRR_CT}`,
                    viewData: coronalViewData,
                    instance,
                    viewName: coronalViewData.viewName,
                  };
                }

                const axialViewData = cachedData.find(
                  view =>
                    view.viewName === MedicalImagesType.AXIAL_RADIODENSITY_CT
                );
                if (axialViewData) {
                  axialView = {
                    key: `${cacheKey}_${MedicalImagesType.AXIAL_RADIODENSITY_CT}`,
                    viewData: axialViewData,
                    instance,
                    viewName: axialViewData.viewName,
                  };
                }
              }
            }
          });
        }
      }

      return {
        sagittalDrrView: sagittalView,
        coronalDrrView: coronalView,
        axialRadiodensityView: axialView,
      };
    }, [
      selectedTwin,
      areViewsReady,
      selectedModality,
      groupedThumbnailsStandard,
      twinId,
      digitalTwinSurgeryCache,
      selectedDrrView,
    ]);

  const selectedView =
    selectedDrrView === "sagittal" ? sagittalDrrView : coronalDrrView;

  const handleToggleDrrView = newState => {
    setSelectedDrrView(newState ? "coronal" : "sagittal");
  };

  const handleManualVertebraChange = useCallback(selectedItem => {
    const value = selectedItem.target
      ? selectedItem.target.value
      : selectedItem.value;
    setSelectedVertebrae(value === "-" ? [] : [value]);
  }, []);

  const handleManualSecondVertebraChange = useCallback(
    selectedItem => {
      const value = selectedItem.target
        ? selectedItem.target.value
        : selectedItem.value;
      if (value === "-") {
        setSelectedVertebrae([]);
        return;
      }

      const firstVertebra = selectedVertebrae[0];
      if (!firstVertebra || firstVertebra === "-") {
        setSelectedVertebrae([]);
        return;
      }

      const firstIndex = vertebraItems.findIndex(
        item => item.value === firstVertebra
      );
      const secondIndex = vertebraItems.findIndex(item => item.value === value);

      if (firstIndex === -1 || secondIndex === -1) {
        setSelectedVertebrae([]);
        return;
      }

      const startIndex = Math.min(firstIndex, secondIndex);
      const endIndex = Math.max(firstIndex, secondIndex);

      const vertebraRange = vertebraItems
        .slice(startIndex, endIndex + 1)
        .map(item => item.value)
        .filter(vertebra => vertebra !== "-");

      const finalRange =
        secondIndex < firstIndex ? vertebraRange.reverse() : vertebraRange;

      setSelectedVertebrae(finalRange);
    },
    [selectedVertebrae, vertebraItems]
  );

  // Update diameter for all selected vertebrae
  const handleDiameterChange = useCallback(
    newValue => {
      const numericValue = Number(newValue);
      if (selectedVertebrae.length === 0) return;

      setVertebraDimensions(prev => {
        const updated = { ...prev };
        selectedVertebrae.forEach(vertebra => {
          const entityId = vertebraUtils.getVertebraId(vertebra);
          if (entityId) {
            updated[entityId] = {
              height: updated[entityId]?.height ?? DEFAULT_HEIGHT,
              diameter: numericValue,
            };
          }
        });
        return updated;
      });
    },
    [selectedVertebrae]
  );

  // Update height for all selected vertebrae
  const handleHeightChange = useCallback(
    newValue => {
      const numericValue = Number(newValue);
      if (selectedVertebrae.length === 0) return;

      setVertebraDimensions(prev => {
        const updated = { ...prev };
        selectedVertebrae.forEach(vertebra => {
          const entityId = vertebraUtils.getVertebraId(vertebra);
          if (entityId) {
            updated[entityId] = {
              height: numericValue,
              diameter: updated[entityId]?.diameter ?? DEFAULT_DIAMETER,
            };
          }
        });
        return updated;
      });
    },
    [selectedVertebrae]
  );

  const handleDiameterIncrement = useCallback(() => {
    const newValue = Math.min(displayedDiameter + 1, 50);
    handleDiameterChange(newValue);
  }, [displayedDiameter, handleDiameterChange]);

  const handleDiameterDecrement = useCallback(() => {
    const newValue = Math.max(displayedDiameter - 1, 1);
    handleDiameterChange(newValue);
  }, [displayedDiameter, handleDiameterChange]);

  const handleHeightIncrement = useCallback(() => {
    const newValue = Math.min(displayedHeight + 1, 100);
    handleHeightChange(newValue);
  }, [displayedHeight, handleHeightChange]);

  const handleHeightDecrement = useCallback(() => {
    const newValue = Math.max(displayedHeight - 1, 1);
    handleHeightChange(newValue);
  }, [displayedHeight, handleHeightChange]);

  const handleToggleBlackAndWhite = useCallback(
    async newState => {
      setBlackAndWhiteProjection(newState);
      if (renderClient) {
        try {
          await renderClient.setRadiodensityProjectionFilter(
            remoteKey,
            !newState
          );
        } catch (error) {}
      }
    },
    [remoteKey, renderClient]
  );

  const selectedAxialVertebraItem = useMemo(() => {
    if (!selectedAxialVertebra || selectedAxialVertebra === "-") {
      return vertebraItems[0];
    }
    const foundItem = vertebraItems.find(
      item => item.value === selectedAxialVertebra
    );
    return foundItem || vertebraItems[0];
  }, [selectedAxialVertebra, vertebraItems]);

  return (
    <div className="radio-density-container">
      <div className="radio-density-sidebar">
        <div className="radio-density-info-and-controls">
          <div className="radio-density-info">
            <div className="radio-density-info-text">
              <div className="radio-density-info-title">
                Adjust Region of Interest (ROI):
              </div>
              <div className="radio-density-info-subtitle">
                The ROI corresponds to the cylinders on the image inside the
                vertebral bodies.
              </div>
            </div>
            <div className="radio-density-controls">
              <LabeledRangeSelect
                label=""
                firstItems={vertebraItems}
                secondItems={vertebraItems.filter(
                  item => item.value !== selectedVertebraItem.value
                )}
                firstSelectedItem={selectedVertebraItem}
                secondSelectedItem={lastSelectedVertebraItem}
                onChangeFirst={handleManualVertebraChange}
                onChangeSecond={handleManualSecondVertebraChange}
                hasOnlyOneItem={false}
              />

              <div className="radio-density-dimension-section">
                <div className="radio-density-dimension-title">
                  Cylinder Diameter
                </div>
                <div className="radio-density-dimension-controls">
                  <LabeledSlider
                    label=""
                    min={1}
                    max={50}
                    value={displayedDiameter}
                    onChange={handleDiameterChange}
                    disabled={selectedVertebrae.length === 0}
                  />
                  <MeasurementDisplay
                    value={displayedDiameter}
                    unit="mm"
                    disabled={selectedVertebrae.length === 0}
                  />
                  <IncrementDecrementButtons
                    onIncrement={handleDiameterIncrement}
                    onDecrement={handleDiameterDecrement}
                    onIncrementEnd={() => {}}
                    onDecrementEnd={() => {}}
                    disabled={selectedVertebrae.length === 0}
                  />
                </div>
              </div>

              <div className="radio-density-dimension-section">
                <div className="radio-density-dimension-title">
                  Cylinder Height
                </div>
                <div className="radio-density-dimension-controls">
                  <LabeledSlider
                    label=""
                    min={1}
                    max={100}
                    value={displayedHeight}
                    onChange={handleHeightChange}
                    disabled={selectedVertebrae.length === 0}
                  />
                  <MeasurementDisplay
                    value={displayedHeight}
                    unit="mm"
                    disabled={selectedVertebrae.length === 0}
                  />
                  <IncrementDecrementButtons
                    onIncrement={handleHeightIncrement}
                    onDecrement={handleHeightDecrement}
                    onIncrementEnd={() => {}}
                    onDecrementEnd={() => {}}
                    disabled={selectedVertebrae.length === 0}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="radio-density-viewer-section">
          <div className="radio-density-orientation-controls">
            <div className="radio-density-orientation-label">
              Projection of CT scan:
            </div>
            <NeutralToggle
              isOn={selectedDrrView === "coronal"}
              onToggle={handleToggleDrrView}
              titlePosition="left-right"
              titleOff="Sag"
              titleOn="Cor"
              disabled={!sagittalDrrView || !coronalDrrView}
            />
          </div>
          <div className="radio-density-viewer">
            {selectedView ? (
              <ImageViewer
                key={`drr-viewer-${selectedView.key}`}
                viewData={selectedView.viewData}
                instance={selectedView.instance}
                onContentLoad={() => {}}
                onBrightnessClick={() => {}}
                viewId={selectedView.viewData.viewId}
                remoteRenderClient={renderClient}
                planId={planId}
                instanceId={selectedView.instance?._id}
                displaySettingsViewType={getDisplaySettingsViewType(selectedView.viewName)}
                persistedDisplaySettings={
                  selectedView.instance?.display_settings?.[
                    getDisplaySettingsViewType(selectedView.viewName)
                  ]
                }
              >
                <RemoteViewer
                  remoteRenderClient={renderClient}
                  viewData={selectedView.viewData}
                  sliderWidth="80%"
                />
              </ImageViewer>
            ) : (
              <div style={{ padding: "20px", color: "#666" }}>
                No DRR views available
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="radio-density-view">
        <div className="radio-density-axial-section">
          <div className="radio-density-axial-viewer">
            {axialRadiodensityView ? (
              <ImageViewer
                key={`axial-radiodensity-viewer-${axialRadiodensityView.key}`}
                viewData={axialRadiodensityView.viewData}
                instance={axialRadiodensityView.instance}
                onContentLoad={() => {}}
                onBrightnessClick={() => {}}
                viewId={axialRadiodensityView.viewData.viewId}
                remoteRenderClient={renderClient}
                planId={planId}
                instanceId={axialRadiodensityView.instance?._id}
                displaySettingsViewType={getDisplaySettingsViewType(axialRadiodensityView.viewName)}
                persistedDisplaySettings={
                  axialRadiodensityView.instance?.display_settings?.[
                    getDisplaySettingsViewType(axialRadiodensityView.viewName)
                  ]
                }
              >
                <RemoteViewer
                  remoteRenderClient={renderClient}
                  viewData={axialRadiodensityView.viewData}
                  sliderWidth="80%"
                />
              </ImageViewer>
            ) : (
              <div style={{ padding: "20px", color: "#666" }}>
                No axial radiodensity view available
              </div>
            )}
            <div className="radio-density-axial-controls-top-left">
              <Select
                items={vertebraItems}
                selectedItem={selectedAxialVertebraItem}
                onChange={handleAxialVertebraChange}
                disable={false}
              />
            </div>
            <div className="radio-density-axial-controls-top-right">
              <div className="radio-density-info-button-container">
                <IconButton
                  icon="Info"
                  tooltipContent={
                    "The color classifications in this viewer are determined exclusively by HU thresholds," +
                    " based on the findings of Gausden et al. (2017), who systematically reviewed the correlation between" +
                    " HU measurements and Dual-energy X-ray Absorptiometry (DXA)-derived Bone Mineral Density (BMD)."
                  }
                  className="radio-density-toggle-info-button"
                  onClick={e => e.stopPropagation()}
                  isCircle
                  size={ButtonSize.MICRO}
                  isLightBlue={false}
                />
              </div>
              <NeutralToggle
                isOn={blackAndWhiteProjection}
                onToggle={handleToggleBlackAndWhite}
                titlePosition="top"
                titleOff="B"
                titleOn="W"
                disabled={false}
              />
            </div>
            <div className="radio-density-description">
              <span className="radio-density-description-text">
                Average radiodensity within the ROI measured along its principal
                axis (cylinder height)
              </span>
              <IconButton
                icon="Info"
                tooltipContent={
                  <div>
                    <p>
                      Average radiodensity within the ROI measured along its
                      principal axis (cylinder height)
                    </p>
                    <img
                      src={VertebraSchemeIcon}
                      alt="Vertebra Scheme"
                      style={{ width: "100%", marginTop: "10px" }}
                    />
                  </div>
                }
                className="radio-density-toggle-info-button"
                onClick={e => e.stopPropagation()}
                isCircle
                size={ButtonSize.MICRO}
                isLightBlue={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RadioDensity;
